// Providers.js
import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
    collection,
    setDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    query,
    where,
    limit,
    serverTimestamp,
} from "firebase/firestore";

const EMPTY_CATEGORY_ROW = {
    categoryId: "",
    commissionType: "percent", // "percent" | "flat"
    commissionValue: "",
    active: true,
    prices: "",
};

// "10%" or "₹50" — how the company's cut is shown.
const formatCommission = (entry) =>
    entry?.commissionType === "flat"
        ? `₹${entry.commissionValue}`
        : `${entry?.commissionValue ?? 0}%`;

const Providers = () => {
    const [name, setName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [selectedUserId, setSelectedUserId] = useState("");
    const [experience, setExperience] = useState("");
    const [image, setImage] = useState("assets/images/all_person.jpg");
    const [averageRatings, setAverageRatings] = useState("");
    const [active, setActive] = useState(true);
    // One row per category the provider works in, each with its own commission.
    const [categoryRows, setCategoryRows] = useState([{ ...EMPTY_CATEGORY_ROW }]);
    const [location, setLocation] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [providers, setProviders] = useState([]);
    const [providerUsers, setProviderUsers] = useState([]);
    const [userSearch, setUserSearch] = useState("");
    const [error, setError] = useState("");
    const [validationErrors, setValidationErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [categoryMap, setCategoryMap] = useState({});
    const [selectedCategoryId, setSelectedCategoryId] = useState("all");

    useEffect(() => {
        const fetchData = async () => {
            await fetchCategoryMap();
            await fetchProviderUsers();
            await fetchProviders();
        };
        fetchData();
    }, []);

    const fetchCategoryMap = async () => {
        try {
            const snapshot = await getDocs(collection(db, "Categories"));
            const map = {};
            snapshot.docs.forEach((doc) => {
                map[doc.id] = doc.data().name;
            });
            setCategoryMap(map);
        } catch (err) {
            console.error("Error fetching categories:", err);
        }
    };

    const fetchProviders = async () => {
        try {
            const snapshot = await getDocs(collection(db, "ServiceProviders"));
            const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
            setProviders(list);
        } catch (err) {
            console.error("Error fetching providers:", err);
        }
    };

    const fetchProviderUsers = async () => {
        try {
            const providerUsersQuery = query(
                collection(db, "users"),
                where("isserviceprovider", "==", true)
            );
            const snapshot = await getDocs(providerUsersQuery);
            const list = snapshot.docs
                .map((doc) => ({ id: doc.id, ...doc.data() }))
                .sort((a, b) =>
                    (a.name || a.fullName || "").localeCompare(b.name || b.fullName || "")
                );
            setProviderUsers(list);
        } catch (err) {
            console.error("Error fetching provider users:", err);
        }
    };

    const getUserPhoneNumber = (user) =>
        user?.phoneNumber || user?.phone_number || "";

    const getUserName = (user) =>
        user?.name || user?.fullName || "Unnamed user";

    const getProviderUserLabel = (user) => {
        if (!user) return "N/A";
        const displayName = getUserName(user);
        const displayPhone = getUserPhoneNumber(user) || "No phone";
        return `${displayName} (${displayPhone})`;
    };

    const getProviderMatchedUserId = (provider) =>
        provider.userId || provider.user_id || "";

    const findProviderUserById = (userId) =>
        providerUsers.find((user) => user.id === userId);

    const findMatchedProviderUser = (provider) =>
        findProviderUserById(getProviderMatchedUserId(provider));

    const selectedProviderUser = findProviderUserById(selectedUserId);

    const filteredProviderUsers = providerUsers.filter((user) => {
        const search = userSearch.trim().toLowerCase();
        if (!search) return true;

        return [
            user.id,
            getUserName(user),
            user.email,
            getUserPhoneNumber(user),
        ].some((value) => value?.toString().toLowerCase().includes(search));
    });

    const visibleProviderUsers =
        selectedProviderUser &&
            !filteredProviderUsers.some((user) => user.id === selectedProviderUser.id)
            ? [selectedProviderUser, ...filteredProviderUsers]
            : filteredProviderUsers;

    const handleMatchedUserChange = (userId) => {
        setSelectedUserId(userId);
        clearFieldError("selectedUserId");
        const matchedUser = providerUsers.find((user) => user.id === userId);

        if (!matchedUser) return;

        setName(matchedUser.name || matchedUser.fullName || "");
        setPhoneNumber(getUserPhoneNumber(matchedUser));
        clearFieldError("name");
        clearFieldError("phoneNumber");
    };

    const clearFieldError = (field) => {
        setValidationErrors((prev) => {
            if (!prev[field]) return prev;

            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const updateRequiredField = (setter, field, value) => {
        setter(value);

        if (value?.toString().trim()) {
            clearFieldError(field);
        }
    };

    const validateProviderForm = () => {
        const requiredFields = {
            selectedUserId: [selectedUserId, "Select a service-provider user."],
            name: [name, "Provider name is required."],
            phoneNumber: [phoneNumber, "Phone number is required."],
            location: [location, "Location is required."],
            image: [image, "Image URL is required."],
        };

        const errors = Object.entries(requiredFields).reduce((errors, [field, [value, message]]) => {
            if (!value?.toString().trim()) {
                errors[field] = message;
            }

            return errors;
        }, {});

        // Rating is optional, but when given it must be a number from 0 to 5.
        if (averageRatings.toString().trim()) {
            const rating = Number(averageRatings);
            if (isNaN(rating) || rating < 0 || rating > 5)
                errors.averageRatings = "Rating must be between 0 and 5.";
        }

        if (selectedUserId && !selectedProviderUser) {
            errors.selectedUserId = "Select a valid service-provider user.";
        }

        // Per-category rows: category chosen once, commission >= 0 (and <= 100
        // for percent).
        const seen = new Set();
        categoryRows.forEach((row, i) => {
            const value = row.commissionValue;
            const num = Number(value);
            let message = "";
            if (!row.categoryId) message = "Select a category.";
            else if (seen.has(row.categoryId)) message = "Category is repeated.";
            else if (value === "" || isNaN(num))
                message = "Enter the commission value.";
            else if (num < 0) message = "Commission cannot be negative.";
            else if (row.commissionType === "percent" && num > 100)
                message = "Percent commission cannot exceed 100.";
            seen.add(row.categoryId);
            if (message) errors[`categoryRow${i}`] = message;
        });

        return errors;
    };

    const updateCategoryRow = (index, patch) => {
        setCategoryRows((rows) =>
            rows.map((row, i) => (i === index ? { ...row, ...patch } : row))
        );
        clearFieldError(`categoryRow${index}`);
    };

    const addCategoryRow = () =>
        setCategoryRows((rows) => [...rows, { ...EMPTY_CATEGORY_ROW }]);

    const removeCategoryRow = (index) =>
        setCategoryRows((rows) =>
            rows.length === 1 ? rows : rows.filter((_, i) => i !== index)
        );

    const getFieldClass = (field, extraClasses = "") =>
        [
            "rounded border p-2 text-gray-900 focus:outline-none focus:ring-1",
            validationErrors[field]
                ? "border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
            extraClasses,
        ]
            .filter(Boolean)
            .join(" ");

    const getLabelClass = (field) =>
        `grid gap-1 text-sm font-medium ${validationErrors[field] ? "text-red-700" : "text-gray-700"}`;

    const renderFieldError = (field) =>
        validationErrors[field] ? (
            <span className="text-xs font-medium text-red-600">
                {validationErrors[field]}
            </span>
        ) : null;

    const resetForm = () => {
        setName("");
        setPhoneNumber("");
        setSelectedUserId("");
        setUserSearch("");
        setExperience("");
        setImage("assets/images/all_person.jpg");
        setAverageRatings("");
        setActive(true);
        setCategoryRows([{ ...EMPTY_CATEGORY_ROW }]);
        setLocation("");
        setEditingId(null);
        setValidationErrors({});
        setError("");
    };

    const handleAddOrUpdateProvider = async (e) => {
        e.preventDefault();
        const providerValidationErrors = validateProviderForm();

        if (Object.keys(providerValidationErrors).length > 0) {
            setValidationErrors(providerValidationErrors);
            setError("Please fill the highlighted required fields.");
            return;
        }

        setLoading(true);
        setError("");
        setValidationErrors({});

        try {
            const matchedUser = providerUsers.find(
                (user) => user.id === selectedUserId
            );

            if (!matchedUser) {
                setError("Select a valid service-provider user.");
                setValidationErrors({
                    selectedUserId: "Select a valid service-provider user.",
                });
                setLoading(false);
                return;
            }

            // The provider doc id is the user's uid, so the pro app can
            // fetch its profile with a single get().
            if (editingId && editingId !== matchedUser.id) {
                setError(
                    "This provider was created under a different id. Delete it and add it again for the selected user."
                );
                setLoading(false);
                return;
            }
            if (!editingId && providers.some((p) => p.id === matchedUser.id)) {
                setError("This user is already a provider. Edit the existing entry.");
                setLoading(false);
                return;
            }

            // categoryIds and categories are always written together so the
            // ids list matches the map keys.
            const categories = {};
            categoryRows.forEach((row) => {
                categories[row.categoryId] = {
                    categoryName: categoryMap[row.categoryId] || "",
                    active: row.active,
                    commissionType: row.commissionType,
                    commissionValue: Number(row.commissionValue),
                    // Optional free text; omitted when blank.
                    ...(row.prices.trim() ? { prices: row.prices.trim() } : {}),
                };
            });

            const providerData = {
                id: matchedUser.id,
                userId: matchedUser.id,
                name,
                phone_number: phoneNumber,
                active,
                experience: experience.trim(),
                image,
                // Optional: omitted when blank so the app falls back to its default.
                ...(averageRatings.toString().trim()
                    ? { average_ratings: parseFloat(averageRatings) }
                    : {}),
                location,
                categoryIds: Object.keys(categories),
                categories,
                updatedAt: serverTimestamp(),
            };

            const providerRef = doc(db, "ServiceProviders", matchedUser.id);
            if (editingId) {
                // updateDoc replaces `categories` wholesale, so removed
                // categories don't linger.
                await updateDoc(providerRef, providerData);
            } else {
                await setDoc(providerRef, {
                    ...providerData,
                    createdAt: serverTimestamp(),
                });
            }

            await updateDoc(doc(db, "users", matchedUser.id), {
                isserviceprovider: true,
            });

            resetForm();
            fetchProviders();
        } catch (err) {
            console.error("Error adding/updating provider:", err);
            setError("Failed to save provider.");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (provider) => {
        const matchedUser = findMatchedProviderUser(provider);

        setName(provider.name);
        setPhoneNumber(provider.phone_number);
        setSelectedUserId(matchedUser?.id || getProviderMatchedUserId(provider));
        setUserSearch("");
        setExperience(provider.experience ?? "");
        setImage(provider.image);
        setAverageRatings(provider.average_ratings ?? "");
        setActive(provider.active !== false);
        const rows = Object.entries(provider.categories || {}).map(
            ([categoryId, entry]) => ({
                categoryId,
                commissionType: entry.commissionType || "percent",
                commissionValue: entry.commissionValue ?? "",
                active: entry.active !== false,
                prices: entry.prices || "",
            })
        );
        setCategoryRows(rows.length ? rows : [{ ...EMPTY_CATEGORY_ROW }]);
        setLocation(provider.location || "");
        setEditingId(provider.id);
    };

    const handleDelete = async (id) => {
        const confirmed = window.confirm(
            "Are you sure you want to delete this service provider?"
        );
        if (!confirmed) return;

        try {
            // Block deletion while the provider still has active jobs.
            const openJobs = await getDocs(
                query(
                    collection(db, "ServiceProviders", id, "assignedBookings"),
                    where("status", "in", ["pending", "confirmed"]),
                    limit(1)
                )
            );
            if (!openJobs.empty) {
                alert(
                    "This provider has pending or confirmed bookings. Reassign or complete them before deleting."
                );
                return;
            }
            await deleteDoc(doc(db, "ServiceProviders", id));
            fetchProviders();
        } catch (err) {
            console.error("Error deleting provider:", err);
            alert(`Failed to delete provider: ${err?.message || "unknown error"}`);
        }
    };

    const filteredProviders =
        selectedCategoryId === "all"
            ? providers
            : providers.filter((p) => p.categoryIds?.includes(selectedCategoryId));

    return (
        <div className="h-screen overflow-y-auto p-4 bg-gray-50">
            <div className="max-w-7xl mx-auto w-full">
                <h1 className="text-3xl font-bold mb-6">Service Providers</h1>

                {error && <p className="text-red-600 mb-4">{error}</p>}

                <form
                    onSubmit={handleAddOrUpdateProvider}
                    className="bg-white border border-gray-200 rounded-md shadow-sm mb-8 overflow-hidden"
                >
                    <div className="flex flex-col gap-3 border-b border-gray-200 p-4 md:flex-row md:items-center md:justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">
                                {editingId ? "Edit Service Provider" : "Add Service Provider"}
                            </h2>
                            <p className="text-sm text-gray-500">
                                {providerUsers.length} eligible user{providerUsers.length === 1 ? "" : "s"}
                            </p>
                        </div>
                        {editingId && (
                            <button
                                type="button"
                                onClick={resetForm}
                                className="self-start rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 md:self-auto"
                            >
                                Cancel Edit
                            </button>
                        )}
                    </div>

                    <div className="space-y-6 p-4">
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                            <label className="grid gap-1 text-sm font-medium text-gray-700">
                                Search provider user
                                <input
                                    type="text"
                                    placeholder="Name, phone, email, or user ID"
                                    className="rounded border border-gray-300 p-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                />
                            </label>
                            <label className={getLabelClass("selectedUserId")}>
                                Matched user
                                <select
                                    className={getFieldClass("selectedUserId")}
                                    value={selectedUserId}
                                    onChange={(e) => handleMatchedUserChange(e.target.value)}
                                >
                                    <option value="">Select service-provider user</option>
                                    {visibleProviderUsers.map((user) => (
                                        <option key={user.id} value={user.id}>
                                            {getProviderUserLabel(user)}
                                        </option>
                                    ))}
                                </select>
                                {renderFieldError("selectedUserId")}
                            </label>
                        </div>

                        <div className="grid grid-cols-1 gap-3 rounded border border-gray-200 bg-gray-50 p-4 text-sm md:grid-cols-2 lg:grid-cols-4">
                            <div>
                                <p className="text-xs font-semibold uppercase text-gray-500">User Name</p>
                                <p className="mt-1 break-words text-gray-900">
                                    {selectedProviderUser ? getUserName(selectedProviderUser) : "Not selected"}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs font-semibold uppercase text-gray-500">Phone</p>
                                <p className="mt-1 break-words text-gray-900">
                                    {selectedProviderUser ? getUserPhoneNumber(selectedProviderUser) || "N/A" : "N/A"}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs font-semibold uppercase text-gray-500">Email</p>
                                <p className="mt-1 break-words text-gray-900">
                                    {selectedProviderUser?.email || "N/A"}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs font-semibold uppercase text-gray-500">User ID</p>
                                <p className="mt-1 break-all text-gray-900">
                                    {selectedProviderUser?.id || "N/A"}
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                            <label className={getLabelClass("name")}>
                                Provider name
                                <input
                                    type="text"
                                    placeholder="Provider name"
                                    className={getFieldClass("name")}
                                    value={name}
                                    onChange={(e) => updateRequiredField(setName, "name", e.target.value)}
                                />
                                {renderFieldError("name")}
                            </label>
                            <label className={getLabelClass("phoneNumber")}>
                                Phone number
                                <input
                                    type="tel"
                                    placeholder="Phone number"
                                    className={getFieldClass("phoneNumber")}
                                    value={phoneNumber}
                                    onChange={(e) => updateRequiredField(setPhoneNumber, "phoneNumber", e.target.value)}
                                />
                                {renderFieldError("phoneNumber")}
                            </label>
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={active}
                                    onChange={(e) => setActive(e.target.checked)}
                                />
                                Active (can be assigned bookings)
                            </label>
                            <label className={getLabelClass("experience")}>
                                Experience (optional)
                                <input
                                    type="text"
                                    placeholder="7 years"
                                    className={getFieldClass("experience")}
                                    value={experience}
                                    onChange={(e) => updateRequiredField(setExperience, "experience", e.target.value)}
                                />
                                {renderFieldError("experience")}
                            </label>
                            <label className={getLabelClass("averageRatings")}>
                                Average rating (optional)
                                <input
                                    type="number"
                                    min="0"
                                    max="5"
                                    step="0.1"
                                    placeholder="4.8"
                                    className={getFieldClass("averageRatings")}
                                    value={averageRatings}
                                    onChange={(e) => updateRequiredField(setAverageRatings, "averageRatings", e.target.value)}
                                />
                                {renderFieldError("averageRatings")}
                            </label>
                            <label className={getLabelClass("location")}>
                                Location
                                <input
                                    type="text"
                                    placeholder="Delhi"
                                    className={getFieldClass("location")}
                                    value={location}
                                    onChange={(e) => updateRequiredField(setLocation, "location", e.target.value)}
                                />
                                {renderFieldError("location")}
                            </label>
                        </div>

                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto]">
                            <label className={getLabelClass("image")}>
                                Image URL
                                <input
                                    type="text"
                                    placeholder="https://example.com/provider.jpg"
                                    className={getFieldClass("image")}
                                    value={image}
                                    onChange={(e) => updateRequiredField(setImage, "image", e.target.value)}
                                />
                                {renderFieldError("image")}
                            </label>
                            {image && (
                                <img
                                    src={image}
                                    alt="Provider preview"
                                    className="h-24 w-24 rounded border border-gray-200 object-cover"
                                />
                            )}
                        </div>


                        {/* Categories this provider works in, each with its own commission */}
                        <section className="space-y-3">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Categories &amp; commission
                                </h3>
                                <button
                                    type="button"
                                    onClick={addCategoryRow}
                                    className="rounded border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    + Add category
                                </button>
                            </div>
                            {categoryRows.map((row, i) => (
                                <div
                                    key={i}
                                    className={`grid grid-cols-1 gap-3 rounded border p-3 md:grid-cols-6 ${validationErrors[`categoryRow${i}`] ? "border-red-500 bg-red-50" : "border-gray-200"}`}
                                >
                                    <select
                                        className="rounded border border-gray-300 p-2 text-gray-900 md:col-span-2"
                                        value={row.categoryId}
                                        onChange={(e) => updateCategoryRow(i, { categoryId: e.target.value })}
                                    >
                                        <option value="">Select category</option>
                                        {Object.entries(categoryMap).map(([id, name]) => (
                                            <option key={id} value={id}>
                                                {name}
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        className="rounded border border-gray-300 p-2 text-gray-900"
                                        value={row.commissionType}
                                        onChange={(e) => updateCategoryRow(i, { commissionType: e.target.value })}
                                    >
                                        <option value="percent">Percent (%)</option>
                                        <option value="flat">Flat (₹)</option>
                                    </select>
                                    <input
                                        type="number"
                                        min="0"
                                        max={row.commissionType === "percent" ? "100" : undefined}
                                        step="any"
                                        placeholder="Commission"
                                        className="rounded border border-gray-300 p-2 text-gray-900"
                                        value={row.commissionValue}
                                        onChange={(e) => updateCategoryRow(i, { commissionValue: e.target.value })}
                                    />
                                    <label className="flex items-center gap-2 text-sm text-gray-700">
                                        <input
                                            type="checkbox"
                                            checked={row.active}
                                            onChange={(e) => updateCategoryRow(i, { active: e.target.checked })}
                                        />
                                        Active
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => removeCategoryRow(i)}
                                        className="text-sm text-red-600 hover:underline"
                                    >
                                        Remove
                                    </button>
                                    <textarea
                                        placeholder="Prices (optional), e.g. Washing 200, Ironing 100, Full Service 500"
                                        className="rounded border border-gray-300 p-2 text-gray-900 md:col-span-6"
                                        value={row.prices}
                                        onChange={(e) => updateCategoryRow(i, { prices: e.target.value })}
                                    />
                                    {renderFieldError(`categoryRow${i}`) && (
                                        <div className="md:col-span-6">{renderFieldError(`categoryRow${i}`)}</div>
                                    )}
                                </div>
                            ))}
                        </section>
                    </div>

                    <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-end">
                        <button
                            type="button"
                            onClick={resetForm}
                            className="rounded border border-gray-300 px-5 py-2 font-medium text-gray-700 hover:bg-white"
                        >
                            Clear
                        </button>
                        <button
                            type="submit"
                            className="rounded bg-green-600 px-6 py-2 font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-green-300"
                            disabled={loading}
                        >
                            {loading ? "Saving..." : editingId ? "Update Provider" : "Add Provider"}
                        </button>
                    </div>
                </form>

                {/* Filter */}
                <div className="mb-6">
                    <label className="font-medium mr-2">Filter by Category:</label>
                    <select
                        className="border rounded p-2"
                        value={selectedCategoryId}
                        onChange={(e) => setSelectedCategoryId(e.target.value)}
                    >
                        <option value="all">All Categories</option>
                        {Object.entries(categoryMap).map(([id, name]) => (
                            <option key={id} value={id}>
                                {name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Providers Table */}
                <div className="w-full border rounded shadow">
                    <table className="min-w-full border-collapse">
                        <thead className="bg-gray-100 sticky top-0 z-10">
                            <tr>
                                <th className="border p-2">Image</th>
                                <th className="border p-2">Name</th>
                                <th className="border p-2">Phone</th>
                                <th className="border p-2">Matched User</th>
                                <th className="border p-2">Experience</th>
                                <th className="border p-2">Avg. Rating</th>
                                <th className="border p-2">Categories (commission)</th>
                                <th className="border p-2">Location</th>
                                <th className="border p-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProviders.length === 0 ? (
                                <tr>
                                    <td colSpan="10" className="text-center p-4">
                                        No providers found.
                                    </td>
                                </tr>
                            ) : (
                                filteredProviders.map((p) => (
                                    <tr key={p.id}>
                                        <td className="border p-2 text-center">
                                            <img
                                                src={p.image}
                                                alt={p.name}
                                                className="w-16 h-16 object-cover rounded mx-auto"
                                            />
                                        </td>
                                        <td className="border p-2">{p.name}</td>
                                        <td className="border p-2">{p.phone_number}</td>
                                        <td className="border p-2">
                                            {getProviderUserLabel(findMatchedProviderUser(p))}
                                        </td>
                                        <td className="border p-2">{p.experience}</td>
                                        <td className="border p-2">{p.average_ratings}</td>
                                        <td className="border p-2">
                                            {p.categoryIds?.length ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {p.categoryIds.map((cid) => {
                                                        const entry = p.categories?.[cid];
                                                        return (
                                                            <span
                                                                key={cid}
                                                                title={entry?.prices || ""}
                                                                className={`rounded-full px-2 py-0.5 text-xs ${entry?.active === false ? "bg-gray-200 text-gray-500" : "bg-blue-100 text-blue-800"}`}
                                                            >
                                                                {entry?.categoryName || categoryMap[cid] || cid} · {formatCommission(entry)}
                                                            </span>
                                                        );
                                                    })}
                                                    {p.active === false && (
                                                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
                                                            inactive
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                "N/A"
                                            )}
                                        </td>
                                        <td className="border p-2">{p.location || "N/A"}</td>
                                        <td className="border p-2 space-x-2">
                                            <button
                                                onClick={() => handleEdit(p)}
                                                className="text-blue-600 hover:underline"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(p.id)}
                                                className="text-red-600 hover:underline"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Providers;
